<template>
  <div
    id="slider"
    ref="scroller"
    :class="['min-h-screen relative h-screen overflow-auto', bakcground]"
  >
    <section
      v-for="(content, index) in runderContents"
      :key="index"
      class="slide flex-col justify-center min-h-screen p-8 overflow-y-scroll"
      :style="{
        'padding-top': `calc(${setyleNavHeight}px + 2rem)`
      }"
      @click="handleClick"
      @dblclick="handleBack"
    >
      <component :is="component(content)" :content="content" @selected="handleSelected" />
    </section>
    <div v-if="sequence.footer" class="pagination">
      <span>{{ currentIndex + 1 }}</span>
      <span>/</span>
      <span>{{ sequenceTotal }}</span>
    </div>
    <div v-if="!progressHidden" class="fixed w-full bg-motif-100 bottom-0">
      <div class="h-1 bg-motif" :style="progress"></div>
    </div>
    <!-- 重新学习按钮组 -->
    <div class="multifunction-slot">
      <slot></slot>
    </div>
  </div>
</template>
<script lang="ts">
import { Vue, Component, Prop, Watch, Mixins } from 'vue-property-decorator';
import { Content, Sequence } from '@forchange/storytime';
// resources
import {
  TextSlide,
  ImageSlide,
  ExplainSlide,
  GetReaderOption,
  StorySlide,
  CourseFinished
} from '@/components/resources';
// models
import Dispatcher from '@/pages/main/models/Dispatcher';
import Slider from '@/pages/main/models/Slider';
import Navigation from '@/pages/main/models/plugins/navigation';
import Touch from '@/pages/main/models/plugins/touch';
import { namespace } from 'vuex-class';
import { getClientIsMobile } from '@/utils/common';
import ScrollListenMixin from '@/mixins/scrollListen';

const userModule = namespace('common');

const dispatcher = new Dispatcher({
  Text: TextSlide,
  Image: ImageSlide,
  Explain: ExplainSlide,
  GetReaderOption: GetReaderOption,
  Story: StorySlide,
  CourseFinished: CourseFinished
});

// 需要等待用户输入的组件
const waitingComponent = ['GetReaderOption'];

let slider: null | Slider = null;

@Component
export default class Slideshow extends Mixins(ScrollListenMixin) {
  @Prop({ type: Object }) sequence!: Sequence & {
    backgroundColor: string;
    progressShow?: '隐藏' | 'hidden';
  };
  @Prop() isInLookBack!: boolean;
  @userModule.State('loading') globalLoading!: boolean;
  @userModule.State('portalSequence') portalSequence!: Array<Sequence>;

  control = {
    waiting: false,
    answer: ''
  };

  currentIndex = 0;

  // 判定但双击事件
  singleTimeout = 0;

  get runderContents() {
    return this.filterContents(this.sequence.contents);
  }

  get occurredContentSize() {
    return window.storyteller.occurredContentIds.size;
  }

  get totalContentSize() {
    return window.storyteller.totalContentIds.size || 1;
  }

  get progress(): Object {
    if (window.storyteller.topContet?.$kind === 'End') {
      return {
        width: '100%'
      };
    }
    return {
      width: `${(this.occurredContentSize / this.totalContentSize) * 100}%`
    };
  }

  get sequenceTotal() {
    if (this.portalView) return this.portalView.contents.length;
    return window.storyteller
      .getWorld()
      .frameStack.top()
      .stmts.filter((item) => !!item.content?.$kind && item.content?.$kind !== 'End').length;
  }

  get portalView() {
    return this.portalSequence[this.portalSequence.length - 1];
  }

  get bakcground() {
    let color;
    switch (this.sequence.backgroundColor) {
      case '白':
      case 'white':
        color = 'bg-white';
        break;

      default:
        color = 'bg-gray-100';
        break;
    }
    return color;
  }

  get progressHidden() {
    return ['隐藏', 'hidden'].includes(this.sequence.progressShow || '');
  }

  private mounted() {
    this.initScrollListen(this.$refs.scroller as HTMLElement);
    this.advanceLoadImage();
    this.init();
    this.onKeyboard();
  }
  async advanceLoadImage() {
    if (this.portalView) {
      this.advanceLoadImageToPortal();
    } else {
      this.advanceLoadImageToStmt();
    }
  }

  async advanceLoadImageToPortal() {
    this.$store.commit('common/setLoading', true);
    const imageList: Array<string> = [];
    this.portalView.contents.forEach((item: Content) => {
      switch (item?.$kind) {
        case 'Image':
          imageList.push((item as Content & { value: string }).value);
          break;
        case 'Explain':
          imageList.push((item as Content & { image: string }).image);
          break;
        case 'Story':
          (item as Content & {
            value: Array<{ image: { path: string }; text: { content: string } }>;
          }).value.forEach((story) => {
            if (story.image) {
              imageList.push(story.image.path);
            }
          });
          break;
        default:
          break;
      }
    });
    try {
      await this.LoadImage(imageList);
    } finally {
      this.$store.commit('common/setLoading', false);
    }
  }

  async advanceLoadImageToStmt() {
    this.$store.commit('common/setLoading', true);
    const topFrameStack = window.storyteller.getWorld().frameStack.top();
    const imageList: Array<string> = [];
    topFrameStack.stmts.forEach((item) => {
      switch (item.content?.$kind) {
        case 'Image':
          imageList.push((item.content as Content & { value: string }).value);
          break;
        case 'Explain':
          imageList.push((item.content as Content & { image: string }).image);
          break;
        case 'Story':
          (item.content as Content & {
            value: Array<{ image: { path: string }; text: { content: string } }>;
          }).value.forEach((story) => {
            if (story.image) {
              imageList.push(story.image.path);
            }
          });
          break;
        default:
          break;
      }
    });
    try {
      await this.LoadImage(imageList);
    } finally {
      this.$store.commit('common/setLoading', false);
    }
  }

  LoadImage(imageList: Array<string>) {
    if (!imageList.length) return;
    return new Promise((resolve) => {
      imageList.forEach((imageUrl) => {
        const newImage = new Image();
        newImage.src = imageUrl;
        newImage.onload = () => {
          resolve('onload');
        };
      });
    });
  }

  private filterContents(contents: Array<Content>): Array<Content> {
    return contents.filter((content) => content.$kind !== 'End');
  }

  private init() {
    slider = new Slider();
    slider.registerPlugins(Navigation, Touch);
    slider.slideTo(this.lastContentIndex);
    this.onTouchEvent(slider);
    this.updateCurrentIndex();
  }

  private onTouchEvent(slider: Slider) {
    const handleEvent = ((event: CustomEvent) => {
      event.detail.direction && this.handleControl(event.detail.direction);
    }) as EventListener;
    slider.getRoot().addEventListener('slide', handleEvent, false);
    this.$once('hook:beforeDestroy', () => {
      slider.getRoot().removeEventListener('slide', handleEvent, false);
    });
  }

  private component(content: Content) {
    if (waitingComponent.indexOf(content.$kind) > -1) {
      this.control.waiting = true;
    } else {
      this.control.waiting = false;
    }
    return dispatcher.dispatch(content);
  }

  private get lastContentIndex(): number {
    return this.runderContents.length - 1;
  }

  private _toLeft() {
    slider?.slidePrevious();
  }

  private _toRight() {
    slider?.slideNext();
  }

  private goTo(type: 'prev' | 'next') {
    const direction = { next: this._toRight, prev: this._toLeft };
    direction[type].call(this);
  }

  private onKeyboard() {
    document.addEventListener('keyup', this.onKeyPress);
    this.$once('hook:beforeDestroy', () => {
      document.removeEventListener('keyup', this.onKeyPress);
    });
  }

  @Watch('sequence', { deep: true })
  handleSequence(seq: Sequence) {
    this.$nextTick(() => {
      slider?.slideTo(this.filterContents(seq.contents).length - 1);
    });
  }

  private updateSeq() {
    if (this.globalLoading) return;
    if (this.portalView) {
      this.$Message.info({
        time: 3000,
        content: `双击退出，${getClientIsMobile() ? '向右滑' : '按下←方向键'}进行回看`
      });
      return;
    }
    if (window.storyteller.topContet?.$kind === 'End') {
      // 向上抛出评分
      this.$PostConnection?.command('rating_course');
      return;
    }
    if (!this.isInLookBack) {
      // 强制添加更新记录, 回看时禁用
      const currentContent = this.sequence.contents[this.currentIndex];
      window.storyteller.updateAssignContent({
        $id: currentContent.$id,
        $kind: currentContent.$kind
      });
    }
    window.next();
  }

  handleBack() {
    clearTimeout(this.singleTimeout);
    const portal = this.portalSequence[this.portalSequence.length - 1] as Sequence & {
      scrollTop: number;
    };
    if (!portal) return;
    this.$store.commit('common/setCallStatus', {
      isroll: false,
      rollHeight: portal.scrollTop
    });
    this.$store.commit('common/setLoading', true);
    this.$store.commit('common/popPortal');
  }

  private handleSelected(answer: string) {
    window.storyteller.input(answer);
    this.control = {
      answer,
      waiting: false
    };
    this.handleControl('next');
  }

  private handleControl(direction: 'prev' | 'next') {
    if (this.control.waiting) {
      console.log('⌛️ 等待用户输入 ...');
      return;
    }

    if (slider?.isLastSlide() && direction === 'next') {
      this.updateSeq();
    } else {
      this.goTo(direction);
    }
    this.currenElTop();
    this.updateCurrentIndex();
  }

  currenElTop() {
    const slider = document.getElementById('slider') as HTMLDivElement;
    if (!slider) return;
    slider.scrollTop = 0;
  }

  handleClick() {
    clearTimeout(this.singleTimeout);
    this.singleTimeout = window.setTimeout(() => {
      this.handleControl('next');
    }, 200);
  }

  private onKeyPress(event: KeyboardEvent) {
    switch (event.code) {
      case 'ArrowUp':
      case 'ArrowLeft':
        this.handleControl('prev');
        break;
      case 'Space':
      case 'ArrowRight':
      case 'ArrowDown':
      case 'Enter':
        this.handleControl('next');
        break;
    }
  }

  updateCurrentIndex() {
    setTimeout(() => {
      this.currentIndex = slider?.getCurrentSlideIndex() || 0;
    }, 10);
  }
}
</script>
<style scoped>
.pagination {
  @apply fixed bottom-4 left-1/2 border rounded-md py-1 pl-3 pr-4 text-xs tracking-widest text-gray-600 bg-gray-100;
  transform: translateX(-50%);
}
.pagination span {
  @apply ml-1;
}

/* slot position */
.multifunction-slot {
  @apply fixed right-4 bottom-16;
}
</style>
